import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useCSVDownloader } from "react-papaparse";

import { getAllPendingPayments, processPayments } from "services";

import { formatNumericCurrency, loadLanguages, useCancelAsyncOnUnmount, useTranslation } from "utils";

import { CREDIT_NOTE_ITEM_TYPES, CREDIT_ONLY_ITEM_TYPES, LANGUAGE_CODE } from "consts";

import { GetPendingPaymentsResponseBody } from "models";

import { AdminHead, AdminTable, Button, Col, Icon, MainPage, Modal, Row, Tooltip, Typography, Message as message } from "components";

import { assignCreditNoteNumbers } from "pages/admin/payments/assignCreditNoteNumbers";
import { buildCreditNoteRows } from "pages/admin/payments/buildCreditNoteRows";

export const PendingPaymentList = () => {
  const { t } = useTranslation("points");

  const [loading, setLoading] = useState(true);
  const [payments, setPayments] = useState<GetPendingPaymentsResponseBody["withBankingDetails"]>([]);
  const [paymentsWithMissingInformation, setPaymentsWithMissingInformation] = useState<GetPendingPaymentsResponseBody["withoutBankingDetails"]>([]);
  const [latestCreditNoteNumber, setLatestCreditNoteNumber] = useState(0);
  const signal = useCancelAsyncOnUnmount()();
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const { CSVDownloader } = useCSVDownloader();

  const [saving, setSaving] = useState(false);
  const [showModalConfirm, setShowModalConfirm] = useState(false);
  const [paymentMode, setPaymentMode] = useState<"full" | "creditsOnly">("full");
  const [templateLanguagesLoaded, setTemplateLanguagesLoaded] = useState(false);

  // Credit note rows need "de"/"en" (Zoho templates) plus each farm's own language (used for the
  // reference field) loaded regardless of the admin's own UI language
  useEffect(() => {
    if (payments.length === 0) return;

    const languages = Array.from(new Set([LANGUAGE_CODE.GERMAN, LANGUAGE_CODE.ENGLISH, ...payments.map(({ language }) => language)]));
    loadLanguages(languages).then(() => setTemplateLanguagesLoaded(true));
  }, [payments]);

  // If none is selected in the table we want to pay all farms
  const selectedPayments = selectedIds.length === 0 ? payments : payments.filter(({ id }) => selectedIds.includes(id));

  // In credits-only mode, footprinting and incentives are paid out externally and must not appear in the CSV/credit note
  const creditNoteItemTypes = paymentMode === "creditsOnly" ? CREDIT_ONLY_ITEM_TYPES : CREDIT_NOTE_ITEM_TYPES;

  // Computed once so the downloaded CSV and the processPayments request always agree on which credit note number a farm got
  const creditNoteNumbersByFarmId = useMemo(
    () => assignCreditNoteNumbers(selectedPayments, latestCreditNoteNumber, creditNoteItemTypes),
    [selectedPayments, latestCreditNoteNumber, creditNoteItemTypes]
  );

  const creditNoteRows = useMemo(() => {
    if (!templateLanguagesLoaded) return { rows: [], error: null };

    try {
      return { rows: buildCreditNoteRows(selectedPayments, creditNoteNumbersByFarmId, t, creditNoteItemTypes), error: null };
    } catch (error) {
      return { rows: [], error: error instanceof Error ? error.message : "Failed to generate the payment CSV." };
    }
  }, [templateLanguagesLoaded, selectedPayments, creditNoteNumbersByFarmId, t, creditNoteItemTypes]);

  useEffect(() => {
    if (creditNoteRows.error) {
      message({ content: creditNoteRows.error, icon: "error" });
    }
  }, [creditNoteRows.error]);

  const loadAllPendingPayments = useCallback(() => {
    getAllPendingPayments({
      signal,
    })
      .then((res) => {
        setPayments(res.withBankingDetails);
        setPaymentsWithMissingInformation(res.withoutBankingDetails);
        setLatestCreditNoteNumber(res.latestCreditNoteNumber);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [signal]);

  useEffect(() => {
    loadAllPendingPayments();
  }, [loadAllPendingPayments]);

  const addPayments = () => {
    setSaving(true);

    processPayments({
      payments: selectedPayments.map(({ farmId, paymentGross }) => ({
        farmId,
        payment: paymentGross,
        creditNoteNumber: creditNoteNumbersByFarmId[farmId] ?? 0,
      })),
      creditsOnly: paymentMode === "creditsOnly",
      signal,
    }).then(({ processedCount }) => {
      loadAllPendingPayments();
      setSaving(false);
      setShowModalConfirm(false);

      message({
        content: `The gremlins have successfully processed ${processedCount} payments.`,
        icon: "gremlin",
      });
    });
  };

  return (
    <MainPage loading={loading} pageName={"admin payments"}>
      <AdminHead
        headline={`${payments.length + paymentsWithMissingInformation.length} pending payment records`}
        description={
          <>
            Select the farms you want to trigger a payout for and then click on the button on the right. If you want to pay out all farms don't select any. Be
            aware that this will not only generate the csv, but also notify the farmers of the payout.
          </>
        }
        extra={
          <div className="right">
            <Tooltip placement="bottom" title="Generate a payment csv">
              <Button
                size="small"
                type="secondary"
                disabled={saving || payments.length === 0 || !templateLanguagesLoaded || !!creditNoteRows.error}
                onClick={() => {
                  setPaymentMode("full");
                  setShowModalConfirm(true);
                }}
              >
                <Icon iconName="tabler/wallet" size="s" />
              </Button>
            </Tooltip>
            <Tooltip placement="bottom" title="Generate a payment csv for credits only (footprinting & incentives paid out externally)">
              <Button
                size="small"
                type="secondary"
                disabled={saving || payments.length === 0 || !templateLanguagesLoaded || !!creditNoteRows.error}
                onClick={() => {
                  setPaymentMode("creditsOnly");
                  setShowModalConfirm(true);
                }}
                className="margin-left-50"
              >
                <Icon iconName="tabler/wallet-disabled" size="s" />
              </Button>
            </Tooltip>
          </div>
        }
      />
      <Row>
        <Col>
          <Typography variant="h3">{payments.length} Pending payment(s) ready to process</Typography>
        </Col>
        <Col>
          {/* @ts-expect-error AdminTable not yet in TS */}
          <AdminTable
            dataSource={payments.map(({ id, farmId, farmName, programName, paymentGross, footprintingPaymentNet, creditPaymentNet, incentivePaymentNet }) => ({
              id,
              farmId,
              farmName,
              programName,
              "payment incl vat": formatNumericCurrency(paymentGross ?? 0),
              "credit payment excl vat": formatNumericCurrency(creditPaymentNet ?? 0),
              "incentive payment excl vat": formatNumericCurrency(incentivePaymentNet ?? 0),
              "footprinting payment excl vat": formatNumericCurrency(footprintingPaymentNet ?? 0),
            }))}
            onRowSelectionChange={(ids: string[]) => setSelectedIds(ids)}
            rowKey={"id"}
            config={{
              exclude: ["id"],
              actions: [
                {
                  icon: "tabler/eye",
                  route: "/farm/",
                  record: "farmId",
                  tooltip: "View this farm",
                },
              ],
            }}
          />
        </Col>
      </Row>
      {paymentsWithMissingInformation.length > 0 && (
        <Row>
          <Col>
            <Typography variant="h3">{paymentsWithMissingInformation.length} Pending payment(s) with missing banking information</Typography>
            <Typography variant="paragraph-m">
              The following payouts have been approved but are missing banking information needed to complete a transaction.
            </Typography>
          </Col>
          <Col>
            {/* @ts-expect-error AdminTable not yet in TS */}
            <AdminTable
              dataSource={paymentsWithMissingInformation}
              rowKey={"id"}
              config={{
                exclude: ["bankingDetails", "id", "programIsInsetting"],
                actions: [
                  {
                    icon: "tabler/eye",
                    route: "/farm/",
                    record: "farmId",
                    tooltip: "View this farm",
                  },
                ],
              }}
            />
          </Col>
        </Row>
      )}
      <CSVDownloader filename={"payments"} delimiter={","} data={creditNoteRows.rows}>
        <Modal
          cancelText="close"
          description={`You have selected ${selectedPayments.length} ${
            selectedPayments.length > 1 ? "farms" : "farm"
          } to pay out. Are you sure you want to proceed? This will trigger notifications to those farms and mark them as paid in the database.${
            paymentMode === "creditsOnly"
              ? " Only credit payments will be included in this csv — footprinting and incentive payments for these farms are being paid out externally."
              : ""
          }`}
          onOk={addPayments}
          onCancel={(e) => {
            setShowModalConfirm(false);
            // Use stop propagation to now trigger csv download
            e.stopPropagation();
          }}
          okText={"proceed"}
          showModal={showModalConfirm}
          title={"Pay out farms"}
          loading={saving}
        />
      </CSVDownloader>
    </MainPage>
  );
};
